export interface LegalSection {
  title: string
  paragraphs: string[]
}

export interface LegalDocument {
  pageTitle: string
  effectiveDate: string
  sections: LegalSection[]
}

export type LegalDocKey = 'userAgreement' | 'privacyPolicy' | 'disclaimer' | 'acceptableUsePolicy'

export const LEGAL_DOC_TABS: Array<{ key: LegalDocKey; label: string }> = [
  { key: 'userAgreement', label: '用戶協議' },
  { key: 'privacyPolicy', label: '隱私政策' },
  { key: 'disclaimer', label: '免責聲明' },
  { key: 'acceptableUsePolicy', label: '可接受使用政策' },
]

export const LEGAL_DOCS: Record<LegalDocKey, LegalDocument> = {
  userAgreement: {
    pageTitle: 'Kyren Pay 商戶用戶協議',
    effectiveDate: '2026-03-02',
    sections: [
      {
        title: '1. 定義與解釋',
        paragraphs: [
          '「服務」指 Kyren Pay 提供之商戶註冊、收款接入、交易處理、風控、對賬、結算及相關技術能力；「拒付」指持卡人或通道發起之撤銷、爭議或追索。',
          '「受限制地區/對象」指受制裁、禁運、反洗錢或其他監管限制之國家、地區、個人或實體；「工作日」指香港法定工作日（不含週末及法定假日）。',
        ],
      },
      {
        title: '2. 平台定位與角色邊界',
        paragraphs: [
          'Kyren Pay 為技術與結算服務平台，除法律另有強制規定外，不是商戶商品或服務之製造方、銷售方或履約保證方。',
          '商戶就商品內容、營銷宣傳、履約交付、售後退款、客訴與稅務合規承擔全部責任。',
        ],
      },
      {
        title: '3. 賬戶開通、訪問權限與安全',
        paragraphs: [
          '商戶應以真實、完整、可驗證資料開通賬戶，並在資料變更時及時更新。',
          '商戶須妥善管理賬戶、密碼及 API Key，不得轉讓、出借或共享；如發現異常應立即通知平台。',
        ],
      },
      {
        title: '4. 商戶資質與 KYC/KYB 合規義務',
        paragraphs: [
          '商戶應配合平台及合作通道開展 KYC/KYB、AML、制裁名單及實益擁有人審查。',
          '拒絕配合、逾期提交或提供虛假資料時，平台可限制功能、延遲結算、凍結或終止服務。',
        ],
      },
      {
        title: '5. 服務使用規則與商戶承諾',
        paragraphs: [
          '商戶不得利用平台從事詐騙、洗錢、制裁規避、侵權盜版、色情、賭博、毒品、黑灰產等違法或禁止行為。',
          '商戶不得代收代付、通道借用、拆單洗單、借殼收款，亦不得以惡意程式或漏洞利用干擾平台安全。',
        ],
      },
      {
        title: '6. 交易處理、費率與稅務責任',
        paragraphs: [
          '商戶同意平台按公告或約定收取費率、固定費及其他適用費項，平台可在合理通知後調整規則。',
          '平台可於結算前扣除服務費、退款、拒付、罰款及其他依法/依約應扣項；商戶自行承擔其直接稅申報義務。',
        ],
      },
      {
        title: '7. 結算、凍結與抵扣',
        paragraphs: [
          '結算需符合平台當期規則（如最低門檻、KYC 狀態、風險狀態等）；如有爭議或調查，平台可延遲結算或凍結資金。',
          '凍結期限可持續至風險解除或調查完成；平台有權就商戶應付款項進行抵扣與追償。',
        ],
      },
      {
        title: '8. 退款、拒付與爭議處理',
        paragraphs: [
          '商戶應在付款前顯著展示退款政策並建立客服機制；對商品履約與退款承擔第一責任。',
          '無卡交易（CNP）天然存在拒付風險，商戶應在指定期限內配合舉證，否則可能視為放棄抗辯。',
        ],
      },
      {
        title: '9. 風險處置機制（先處置後通知）',
        paragraphs: [
          '遇異常交易、可疑欺詐、制裁風險、投訴激增、拒付率異常、通道或監管要求時，平台可先行處置後通知。',
          '處置措施包括拒絕交易、限額、延遲結算、凍結、抵扣、暫停或終止服務。',
        ],
      },
      {
        title: '10. 知識產權、保密與數據保護',
        paragraphs: [
          '平台軟體、介面、文件及標識之知識產權歸平台或授權方所有；商戶僅可在授權範圍內使用。',
          '雙方對合作中知悉之非公開資訊負保密義務；數據處理應符合隱私政策與適用法律。',
        ],
      },
      {
        title: '11. 免責、責任限制與賠償',
        paragraphs: [
          '服務按「現狀」與「可用」提供；對第三方通道、銀行、雲服務、網路故障引致之影響，平台在法律允許範圍內不承擔責任。',
          '平台責任上限採固定金額與近 12 個月服務費兩者取高；商戶違約或侵權導致平台損失時應承擔全額賠償責任。',
        ],
      },
      {
        title: '12. 協議期限、更新、終止與聯絡方式',
        paragraphs: [
          '平台可因法律、監管、通道規則或風控需求更新協議；重大更新將公告或通知，繼續使用視為接受新版本。',
          '本協議受香港法律管轄，爭議應先協商，協商不成提交香港有管轄權法院；聯絡郵箱：admin@kyren.io。',
        ],
      },
    ],
  },
  privacyPolicy: {
    pageTitle: 'Kyren Pay 隱私政策',
    effectiveDate: '2026-03-02',
    sections: [
      {
        title: '1. 資料控制者與適用範圍',
        paragraphs: [
          '本政策適用於商戶、買方、網站訪問者及其他與 Kyren Pay 聯絡之人士。',
          '資料控制者為啟潤科技有限公司 / KYREN TECHNOLOGY LIMITED，聯絡郵箱：admin@kyren.io。',
        ],
      },
      {
        title: '2. 我們收集的資料類型',
        paragraphs: [
          '我們可能收集商戶賬戶資料、交易與支付資料、KYC/KYB 資料、結算與銀行賬戶資料，以及必要的裝置與安全日誌資訊。',
          '敏感資訊將採取加密、脫敏或最小化保存策略。',
        ],
      },
      {
        title: '3. 資料來源',
        paragraphs: [
          '資料可能來自您直接提交、交易過程自動產生、合作支付機構回傳或法律允許之公開來源。',
          '您需確保向平台提供之資料合法、真實且具備必要授權。',
        ],
      },
      {
        title: '4. 處理目的與法律基礎',
        paragraphs: [
          '我們基於履行合同、法律義務、正當利益或同意等基礎處理資料。',
          '處理目的包括賬戶管理、交易處理、風險控制、合規審查、審計與客服支援。',
        ],
      },
      {
        title: '5. 自動化風險決策與人工覆核',
        paragraphs: [
          '為防範欺詐、洗錢與拒付風險，我們可能使用規則引擎或模型對交易與賬戶進行風險評估。',
          '對重大影響決策，在合法可行範圍內可提供人工覆核（受法規與安全限制）。',
        ],
      },
      {
        title: '6. 資料共享',
        paragraphs: [
          '在必要範圍內，我們可能向支付機構、銀行、雲服務商、專業顧問及依法有權機關共享資料。',
          '我們不以出售個人資料為目的進行資料交易。',
        ],
      },
      {
        title: '7. 跨境傳輸',
        paragraphs: [
          '因跨境支付與技術架構，資料可能在香港以外司法區被處理或儲存。',
          '我們將採取合理保護措施以保障跨境資料安全。',
        ],
      },
      {
        title: '8. 保存期限',
        paragraphs: [
          '我們僅在必要期間保存資料，並可能因審計、爭議、訴訟或法定義務延長留存。',
          '常見保存範圍包括交易與結算核心資料、安全日誌、回調日誌與備份資料。',
        ],
      },
      {
        title: '9. Cookie 與類似技術',
        paragraphs: [
          '我們可能使用必要 Cookie 維持登入與基礎功能，分析/行銷 Cookie 在適用法規下需取得同意。',
          '您可透過瀏覽器或站點設定調整 Cookie 偏好。',
        ],
      },
      {
        title: '10. 安全措施',
        paragraphs: [
          '我們採取密碼雜湊、敏感資料加密、最小權限、審計追蹤、備份與安全監控等措施。',
          '但任何網路傳輸或儲存系統均無法保證絕對安全。',
        ],
      },
      {
        title: '11. 您的權利',
        paragraphs: [
          '在適用法律下，您可申請查詢、更正、刪除、限制處理或撤回同意。',
          '若請求影響 AML、風控、審計或法定留存義務，我們可依法拒絕或延後處理。',
        ],
      },
      {
        title: '12. 未成年人、第三方與政策更新',
        paragraphs: [
          '本服務不面向未成年人；若發現未成年人資料，我們可能採取限制或刪除措施。',
          '平台可能包含第三方連結或整合第三方服務；第三方資料處理行為受其自身政策約束。',
          '本政策可能更新，重大變更將透過公告或郵件通知；權利申請請聯絡：admin@kyren.io。',
        ],
      },
    ],
  },
  disclaimer: {
    pageTitle: 'Kyren Pay 免責聲明',
    effectiveDate: '2026-03-02',
    sections: [
      {
        title: '1. 服務基礎',
        paragraphs: [
          'Kyren Pay 服務按「現狀」與「可用」原則提供，不保證絕對不中斷、無錯誤或完全符合所有特定目的。',
        ],
      },
      {
        title: '2. 第三方依賴',
        paragraphs: [
          '支付機構、銀行、雲服務、網路與通訊等第三方系統故障、延遲或政策變更可能影響交易與結算。',
          '在法律允許範圍內，平台對此類第三方因素導致之損失不承擔責任。',
        ],
      },
      {
        title: '3. 商戶責任邊界',
        paragraphs: [
          '商戶應自行承擔商品或服務內容、履約、售後、投訴與退款相關責任。',
          '平台不對商戶與買方之間的商業爭議結果、商品效果或交付品質提供擔保。',
        ],
      },
      {
        title: '4. 風險與處置',
        paragraphs: [
          '為履行合規與風控義務，平台可先行處置（限額、延遲、凍結、抵扣、暫停）後通知。',
        ],
      },
      {
        title: '5. 不可抗力',
        paragraphs: [
          '因自然災害、戰爭、政府行為、重大網路故障或其他不可抗力導致之服務中斷，平台在法律允許範圍內不承擔責任。',
        ],
      },
      {
        title: '6. 非法律/稅務意見',
        paragraphs: [
          '平台提供之範本、指引、FAQ 與操作說明僅供參考，不構成法律、稅務或投資建議。',
          '商戶應自行諮詢專業顧問並承擔相應決策責任。',
        ],
      },
      {
        title: '7. 責任限制',
        paragraphs: [
          '在法律允許範圍內，平台不承擔間接、附帶、懲罰性或後果性損失。',
          '如依法需承擔責任，總責任上限採固定金額與近 12 個月服務費兩者取高。',
        ],
      },
      {
        title: '8. 交易風險提示',
        paragraphs: [
          '無卡交易（CNP）天然具有拒付與爭議風險，商戶應自行建立風控、客服與退款機制。',
          '退款、拒付、通道處罰及調查費等成本原則上由商戶承擔（法律另有規定者除外）。',
        ],
      },
      {
        title: '9. 內容與資訊免責',
        paragraphs: [
          '平台提供之範本、FAQ、指引與運營建議僅供參考，不構成法律、稅務或投資意見。',
          '商戶應自行尋求專業顧問意見並對其商業決策承擔責任。',
        ],
      },
      {
        title: '10. 與其他文件的關係',
        paragraphs: [
          '本聲明為商戶用戶協議之組成部分；如就免責事項存在衝突，以本聲明優先（法律另有強制規定者除外）。',
        ],
      },
      {
        title: '11. 聯絡方式',
        paragraphs: [
          '啟潤科技有限公司 / KYREN TECHNOLOGY LIMITED。',
          '聯絡郵箱：admin@kyren.io；地址：2/F YAU TAK BUILDING 167 LOCKHART ROAD WAN CHAI HK。',
        ],
      },
    ],
  },
  acceptableUsePolicy: {
    pageTitle: 'Kyren Pay 可接受使用政策（AUP）',
    effectiveDate: '2026-03-02',
    sections: [
      {
        title: '1. 執行模型',
        paragraphs: [
          'Kyren Pay 採用「明確禁止 + 高風險預審」雙層管理機制。',
          '未通過預審的高風險業務，不得接入或持續使用服務。',
        ],
      },
      {
        title: '2. 明確禁止（零容忍）',
        paragraphs: [
          '禁止業務包含但不限於：色情、賭博、毒品、武器、侵權盜版、黑灰產、洗錢、制裁規避、代收代付。',
          '禁止拆單、洗單、借殼收款、通道借用、虛假交易或其他規避風控行為。',
        ],
      },
      {
        title: '3. 高風險預審',
        paragraphs: [
          '金融投資、虛擬資產、醫療健康、跨境藥品、高客訴數位服務、預付儲值型業務等需預審。',
          '平台可要求補充受益人資料、合規證明、履約與退款機制等文件。',
        ],
      },
      {
        title: '4. 受限地區與對象',
        paragraphs: [
          '商戶不得向受制裁國家/地區、受制裁個人或實體提供服務，亦不得協助規避制裁。',
          '平台可基於制裁清單更新與風險評估即時限制交易。',
        ],
      },
      {
        title: '5. 商戶持續義務',
        paragraphs: [
          '商戶需保持資料真實、可驗證，並配合 KYC/KYB、風控調查與監管問詢。',
          '商戶需在付款前顯著展示主體資訊、退款政策與聯絡方式。',
        ],
      },
      {
        title: '6. 違規處置',
        paragraphs: [
          '對違規或高風險行為，平台可先行採取拒絕交易、限額、延遲結算、凍結、抵扣、暫停或終止等措施。',
          '凍結期限可持續至風險解除或調查完成，必要時可依法向有權機關報告。',
        ],
      },
      {
        title: '7. 監測、審查與取證',
        paragraphs: [
          '平台可對交易模式與風險指標進行監測、抽查與人工覆核，並要求商戶限期補充證據材料。',
          '商戶逾期未回覆或拒不配合，平台可視為高風險違規並升級處置。',
        ],
      },
      {
        title: '8. 申訴與復審',
        paragraphs: [
          '商戶如對處置結果有異議，可依平台流程提交申訴與補充證據申請復審。',
          '平台將依事實、證據完整性與監管要求決定是否解除、維持或升級處置。',
        ],
      },
      {
        title: '9. 與其他文件的關係',
        paragraphs: [
          '本政策為《商戶用戶協議》組成部分；若就可接受使用與風險控制事項存在衝突，以本政策為準。',
        ],
      },
      {
        title: '10. 聯絡方式',
        paragraphs: [
          '如對 AUP 或預審要求有疑問，請聯絡：admin@kyren.io。',
        ],
      },
    ],
  },
}
